import React, { useEffect, useState } from "react";
import {
  Button,
  Select,
  Textarea,
  Table,
  Loader,
  Container,
  TextInput,
  Group,
  Card,
  Stack,
  ActionIcon,
  Alert,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash, IconAlertCircle } from "@tabler/icons-react";
import useTransientStore from "../../data/transcientStore";
import { TranslationService } from "../../services/TranslationService";
import { useTranslation } from "../../hooks/useTranslation";

export default function TranslationsPage() {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 319, addressSystemId: addrIdFromStore || 0, autoLoad: true });
  const currentUserAddressSystems = useTransientStore((state) => state.currentUserAddressSystems);
  const [pages, setPages] = useState<any[]>([]);
  const [addressSystems, setAddressSystems] = useState<any[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [selectedPage, setSelectedPage] = useState<string>("");
  const [selectedContract, setSelectedContract] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [texts, setTexts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // New translation form state
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTextCode, setNewTextCode] = useState("");
  const [newPageText, setNewPageText] = useState("");
  const [creatingNew, setCreatingNew] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const currentUserId = useTransientStore((state) => state.currentUserId);

  // Fetch dropdown data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pagesData, addressSystemsData, languagesData] = await Promise.all([
          TranslationService.getPages(),
          TranslationService.getAddressSystems(),
          TranslationService.getLanguages(),
        ]);
        setPages(pagesData as any[]);
        setAddressSystems(addressSystemsData as any[]);
        setLanguages(languagesData as any[]);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        setMessage({ type: "error", text: getTranslation("LABEL_TRANSLATIONS_LOAD_PAGE_DATA_ERROR") || "Error loading page data" });
      }
    };
    fetchData();
  }, [currentUserId]);

  // Fetch translations when selection changes
  useEffect(() => {
    if (selectedPage && selectedContract && selectedLanguage) {
      setLoading(true);
      const fetchTranslations = async () => {
        try {
          const data = await TranslationService.getTranslations(
            parseInt(selectedPage),
            parseInt(selectedContract),
            parseInt(selectedLanguage)
          );
          setTexts(data as any[]);
          setMessage(null);
        } catch (error) {
          console.error("Error fetching translations:", error);
          setTexts([]);
          setMessage({ type: "error", text: getTranslation("LABEL_TRANSLATIONS_LOAD_TRANSLATIONS_ERROR") || "Error loading translations" });
        } finally {
          setLoading(false);
        }
      };
      fetchTranslations();
    }
  }, [selectedPage, selectedContract, selectedLanguage]);

  const handleTextChange = (idx: number, field: string, value: string) => {
    setTexts((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await TranslationService.saveTranslations(
        texts,
        parseInt(selectedPage),
        parseInt(selectedContract),
        parseInt(selectedLanguage)
      );
      notifications.show({
        title: getTranslation("LABEL_TRANSLATIONS_SUCCESS_TITLE") || "Success",
        message: getTranslation("LABEL_TRANSLATIONS_SAVED_SUCCESS") || "Translations saved successfully!",
        color: 'green',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-success'
        // position: 'top-center'
      })
    } catch (error) {
      console.error("Error saving translations:", error);
      setMessage({ type: "error", text: getTranslation("LABEL_TRANSLATIONS_SAVE_ERROR") || "Error saving translations" });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNew = async () => {
    if (!newTextCode.trim()) {
      setMessage({ type: "error", text: getTranslation("LABEL_TRANSLATIONS_TEXT_CODE_REQUIRED") || "Text code is required" });
      return;
    }

    if (!newPageText.trim()) {
      setMessage({ type: "error", text: getTranslation("LABEL_TRANSLATIONS_PAGE_TEXT_REQUIRED") || "Page text is required" });
      return;
    }

    setCreatingNew(true);
    try {
      await TranslationService.createTranslation(
        parseInt(selectedPage),
        parseInt(selectedContract),
        parseInt(selectedLanguage),
        newTextCode,
        newPageText
      );

      // Add new translation to the list
      setTexts((prev) => [
        ...prev,
        {
          id: Math.max(...prev.map((t) => t.id), 0) + 1,
          textCode: newTextCode,
          commonPageTextEng: newPageText,
          commonPageText: "",
          contractPageText: "",
        },
      ]);

      // Reset form
      setNewTextCode("");
      setNewPageText("");
      setShowNewForm(false);
      notifications.show({
        title: getTranslation("LABEL_TRANSLATIONS_SUCCESS_TITLE") || "Success",
        message: getTranslation("LABEL_TRANSLATIONS_SAVED_SUCCESS") || "Translations saved successfully!",
        color: 'green',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-success'
        // position: 'top-center'
      })
    } catch (error) {
      console.error("Error creating translation:", error);
      setMessage({ type: "error", text: getTranslation("LABEL_TRANSLATIONS_CREATE_ERROR") || "Error creating translation" });
    } finally {
      setCreatingNew(false);
    }
  };

  return (
    <div style={{ height: "100vh", overflow: "auto" }}>
      <Container size="xl" py="xl">
        <h1>{getTranslation("LABEL_TRANSLATIONS_TITLE") || "Translations"}</h1>

        {message && (
          <Alert
            icon={message.type === "error" ? <IconAlertCircle /> : undefined}
            title={message.type === "success" ? (getTranslation("LABEL_TRANSLATIONS_SUCCESS_TITLE") || "Success") : (getTranslation("LABEL_TRANSLATIONS_ERROR_TITLE") || "Error")}
            color={message.type === "success" ? "green" : "red"}
            mb="md"
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        <div
          style={{
            display: "flex",
            gap: 16,
            marginBottom: 24,
            flexWrap: "wrap",
          }}
        >
          <Select
            label={getTranslation("LABEL_PAGE") || "Page"}
            placeholder={getTranslation("PLACEHOLDER_SELECT_PAGE") || "Select page"}
            searchable
            data={pages.map((p) => ({ value: p.id.toString(), label: p.pageUrl }))}
            value={selectedPage}
            onChange={(val) => setSelectedPage(val || "")}
            style={{ flex: 1, minWidth: 200 }}
          />
          <Select
            label={getTranslation("LABEL_ADDRESS_SYSTEM") || "Address System"}
            placeholder={getTranslation("PLACEHOLDER_SELECT_ADDRESS_SYSTEM") || "Select address system"}
            searchable
            data={currentUserAddressSystems.map((c) => ({
              value: c.id.toString(),
              label: `${c.name} (ID: ${c.id})`,
            }))}
            value={selectedContract}
            onChange={(val) => setSelectedContract(val || "")}
            style={{ flex: 1, minWidth: 200 }}
          />
          <Select
            label={getTranslation("LABEL_LANGUAGE") || "Language"}
            placeholder={getTranslation("PLACEHOLDER_SELECT_LANGUAGE") || "Select language"}
            searchable
            data={languages.map((l) => ({ value: l.id.toString(), label: l.name }))}
            value={selectedLanguage}
            onChange={(val) => setSelectedLanguage(val || "")}
            style={{ flex: 1, minWidth: 200 }}
          />
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Loader />
          </div>
        ) : texts.length > 0 || selectedPage ? (
          <>
            {/* New Translation Form */}
            {selectedPage && selectedContract && selectedLanguage && (
              <>
                {/* {!showNewForm && (
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setShowNewForm(true)}
                    mb="md"
                  >
                    {getTranslation("LABEL_TRANSLATIONS_ADD_NEW") || "Add New Translation"}
                  </Button>
                )} */}

                {showNewForm && (
                  <Card withBorder mb="md" p="md" style={{ backgroundColor: "#f8f9fa" }}>
                    <Card.Section withBorder inheritPadding py="md">
                      <h3>{getTranslation("LABEL_TRANSLATIONS_CREATE_NEW") || "Create New Translation"}</h3>
                    </Card.Section>

                    <Stack gap="md">
                      <TextInput
                        label={getTranslation("LABEL_TEXT_CODE") || "Text Code"}
                        placeholder={getTranslation("PLACEHOLDER_EG_TEXTLABELOTSIKKO") || "e.g., TEXT_LABEL_OTSIKKO"}
                        value={newTextCode}
                        onChange={(e) => setNewTextCode(e.target.value)}
                        disabled={creatingNew}
                      />

                      <Textarea
                        label={getTranslation("LABEL_PAGE_TEXT") || "Page Text"}
                        placeholder={getTranslation("PLACEHOLDER_ENTER_THE_TRANSLATION_TEXT") || "Enter the translation text..."}
                        value={newPageText}
                        onChange={(e) => setNewPageText(e.target.value)}
                        minRows={3}
                        disabled={creatingNew}
                      />

                      <Group>
                        <Button
                          onClick={handleCreateNew}
                          loading={creatingNew}
                        >
                          {getTranslation("LABEL_TRANSLATIONS_CREATE") || "Create"}
                        </Button>
                        <Button
                          variant="default"
                          onClick={() => {
                            setShowNewForm(false);
                            setNewTextCode("");
                            setNewPageText("");
                          }}
                          disabled={creatingNew}
                        >
                          {getTranslation("LABEL_TRANSLATIONS_CANCEL") || "Cancel"}
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                )}
              </>
            )}

            {/* Translations Table */}
            {texts.length > 0 ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                <div style={{ overflowX: "auto", height: "60vh" }}>
                  <Table striped highlightOnHover>
                    <thead>
                      <tr>
                        <th>{getTranslation("LABEL_TRANSLATIONS_TEXT_CODE") || "Text Code"}</th>
                        <th>{getTranslation("LABEL_TRANSLATIONS_COMMON_TEXT_EN") || "Common Text (English)"}</th>
                        <th>{getTranslation("LABEL_TRANSLATIONS_COMMON_TEXT_SELECTED") || "Common Text (Selected Language)"}</th>
                        <th>{getTranslation("LABEL_TRANSLATIONS_ADDRESS_SYSTEM_TEXT") || "Address System Text"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {texts.map((t, idx) => (
                        <tr key={t.textCode}>
                          <td style={{ fontWeight: 600 }}>{t.textCode}</td>
                          <td>
                            <Textarea
                              value={t.commonPageTextEng || ""}
                              autosize
                              minRows={2}
                              onChange={(e) =>
                                handleTextChange(idx, "commonPageTextEng", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <Textarea
                              value={t.commonPageText || ""}
                              autosize
                              minRows={2}
                              onChange={(e) =>
                                handleTextChange(idx, "commonPageText", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <Textarea
                              value={t.contractPageText || ""}
                              autosize
                              minRows={2}
                              onChange={(e) =>
                                handleTextChange(idx, "contractPageText", e.target.value)
                              }
                              placeholder={getTranslation("PLACEHOLDER_ENTER_ADDRESS_SYSTEM_SPECIFIC_TEXT") || "Enter address system specific text..."}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <Button type="submit" mt="xl" loading={saving}>
                  {getTranslation("LABEL_TRANSLATIONS_SAVE_CHANGES") || "Save Changes"}
                </Button>
              </form>
            ) : (
              <div style={{ padding: 20, textAlign: "center", color: "#666" }}>
                {getTranslation("LABEL_TRANSLATIONS_NONE_FOUND") || "No translations found for this selection. Create a new one to get started!"}
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: 20, textAlign: "center", color: "#999" }}>
            {getTranslation("LABEL_TRANSLATIONS_SELECT_PROMPT") || "Select a page, address system and language to view translations."}
          </div>
        )}
      </Container>
    </div>
  );
}